import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Lock, FileText, Download, ShieldCheck } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { PortalLayout } from "@/components/layout/PortalLayout";
import { GlassCard } from "@/components/GlassCard";
import { SectorBadge } from "@/components/Badges";
import api, { BACKEND } from "@/lib/api";
import { goldBtn, goldBtnSm, glassBtn } from "@/lib/constants";

export default function DealDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [deal, setDeal] = useState(null);
  const [ndaOpen, setNdaOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get(`/deals/${id}`).then((r) => setDeal(r.data)).catch(() => toast.error("Deal not found")), [id]);
  useEffect(() => { load(); }, [load]);

  const requestAccess = async () => {
    try { await api.post("/deals/request-access", { deal_id: id }); toast.success("Access requested."); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const acceptNda = async () => {
    setBusy(true);
    try { await api.post("/deals/nda/accept", { deal_id: id }); toast.success("NDA accepted. Documents unlocked."); setNdaOpen(false); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const requestIntro = async () => {
    try { await api.post("/deals/request-introduction", { deal_id: id }); toast.success("Introduction requested. The desk will coordinate."); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const download = async (docId) => {
    try {
      const { data } = await api.post("/deals/documents/signed-url", { deal_id: id, doc_id: docId });
      window.open(`${BACKEND}${data.signed_url}`, "_blank");
    } catch (e) { toast.error(e?.response?.data?.detail || "Download unavailable"); }
  };

  if (!deal) return <PortalLayout title="Deal"><p className="text-sm text-muted-foreground">Loading…</p></PortalLayout>;

  return (
    <PortalLayout title="Deal Detail">
      <button onClick={() => navigate("/deals")} className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6">
        <ArrowLeft size={15} /> Deal board
      </button>

      <div className="grid gap-8 lg:grid-cols-12">
        <div className="lg:col-span-8">
          <SectorBadge sector={deal.sector} />
          <h1 className="mt-4 pdg-serif text-4xl text-foreground leading-tight">{deal.title}</h1>
          <div className="mt-2 text-sm text-muted-foreground">{deal.deal_type} &middot; {deal.geography} &middot; {deal.size_range}</div>

          {deal.locked ? (
            <GlassCard className="mt-8 p-8 text-center">
              <Lock className="mx-auto text-[hsl(var(--pdg-gold))]" size={26} strokeWidth={1.5} />
              <h2 className="mt-4 pdg-serif text-2xl text-foreground">This deal is restricted</h2>
              <p className="mt-2 text-sm text-muted-foreground max-w-md mx-auto">{deal.teaser}</p>
              <div className="mt-6">
                {deal.access_requested ? (
                  <span className="text-sm text-muted-foreground">Access request pending review.</span>
                ) : (
                  <button onClick={requestAccess} data-testid="deal-detail-request-access" className={goldBtn}>Request Access</button>
                )}
              </div>
            </GlassCard>
          ) : (
            <>
              <div className="mt-8 pdg-hairline w-20" />
              <p className="mt-8 text-base text-foreground/85 leading-8">{deal.description}</p>

              <div className="mt-10">
                <div className="pdg-kicker mb-4">Documents</div>
                {!deal.nda_accepted ? (
                  <GlassCard className="p-6 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <ShieldCheck size={20} className="text-[hsl(var(--pdg-gold))]" strokeWidth={1.5} />
                      <p className="text-sm text-muted-foreground">Accept the NDA to unlock the data room.</p>
                    </div>
                    <button onClick={() => setNdaOpen(true)} data-testid="nda-gate-open-button" className={goldBtnSm}>Review NDA</button>
                  </GlassCard>
                ) : (
                  <div className="space-y-3">
                    {(deal.documents || []).map((doc) => (
                      <div key={doc.id} className="flex items-center justify-between p-4 rounded-xl border border-border bg-[hsl(var(--pdg-elevated)/0.4)]">
                        <div className="flex items-center gap-3">
                          <FileText size={18} className="text-muted-foreground" />
                          <div>
                            <div className="text-sm text-foreground">{doc.name}</div>
                            <div className="text-xs text-muted-foreground">{doc.kind} &middot; {doc.pages} pp</div>
                          </div>
                        </div>
                        <button onClick={() => download(doc.id)} data-testid="deal-document-download-button" className={glassBtn + " h-9 px-3 text-xs"}>
                          <Download size={14} /> Download
                        </button>
                      </div>
                    ))}
                    {(!deal.documents || deal.documents.length === 0) && <p className="text-sm text-muted-foreground">No documents for this deal yet.</p>}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <aside className="lg:col-span-4">
          {!deal.locked && (
            <GlassCard className="p-6">
              <div className="pdg-kicker mb-5">Key metrics</div>
              <dl className="space-y-3 text-sm">
                {Object.entries(deal.key_metrics || {}).map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-4 border-b border-border pb-3">
                    <dt className="text-muted-foreground">{k}</dt><dd className="text-foreground text-right">{v}</dd>
                  </div>
                ))}
                {Object.keys(deal.key_metrics || {}).length === 0 && <p className="text-muted-foreground">Metrics available on request.</p>}
              </dl>
              <button onClick={requestIntro} data-testid="deal-request-introduction-button" className={goldBtn + " w-full mt-6"}>
                Request Introduction
              </button>
            </GlassCard>
          )}
        </aside>
      </div>

      <Dialog open={ndaOpen} onOpenChange={setNdaOpen}>
        <DialogContent className="bg-[hsl(var(--pdg-charcoal))] border-border">
          <DialogHeader>
            <DialogTitle className="pdg-serif text-2xl">Non-Disclosure Agreement</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              To view documents for this opportunity you must accept the terms of confidentiality.
              Your acceptance is recorded with a timestamp and IP address.
            </DialogDescription>
          </DialogHeader>
          <div className="text-sm text-foreground/80 leading-7 max-h-48 overflow-y-auto border border-border rounded-lg p-4">
            You agree to hold all information relating to this opportunity in strict confidence, to use it
            solely for the purpose of evaluating a potential transaction, and not to disclose it to any third
            party without the desk&rsquo;s prior written consent. Documents are watermarked to you.
          </div>
          <DialogFooter className="gap-3">
            <button onClick={() => setNdaOpen(false)} data-testid="nda-gate-decline-button" className={glassBtn + " h-10"}>Decline</button>
            <button onClick={acceptNda} disabled={busy} data-testid="nda-gate-confirm-button" className={goldBtn + " h-10"}>{busy ? "Recording…" : "Accept NDA"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PortalLayout>
  );
}
